<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import TaxiDriversAPI from 'dashboard/api/taxiDrivers';
import TaxiZonesAPI from 'dashboard/api/taxiZones';
import TaxiPartnersAPI from 'dashboard/api/taxiPartners';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const { t } = useI18n();

const drivers = ref([]);
const loading = ref(false);
const searchQuery = ref('');
const driverDialogRef = ref(null);
const editing = ref(null);

const form = ref({
  name: '',
  phone: '',
  driver_segment: 'taxis',
  license_number: '',
  vehicle_plate: '',
  status: 'active',
  notes: '',
});

const zonesCatalog = ref([]);
const selectedZones = ref([]);
const partnersCatalog = ref([]);
const selectedPartners = ref([]);

const syncSelectedZonesFromDriver = driver => {
  const raw = driver.taxi_zones || [];
  const byId = new Map(zonesCatalog.value.map(z => [z.id, z]));
  selectedZones.value = raw.map(
    z => byId.get(z.id) || { id: z.id, name: z.name }
  );
};

const syncSelectedPartnersFromDriver = driver => {
  const raw = driver.taxi_partners || [];
  const byId = new Map(partnersCatalog.value.map(p => [p.id, p]));
  selectedPartners.value = raw.map(
    p => byId.get(p.id) || { id: p.id, name: p.name }
  );
};

const statusOptions = computed(() => [
  {
    value: 'active',
    label: t('TAXIS.DRIVERS.STATUS.ACTIVE'),
    color: 'green',
    icon: 'i-lucide-circle-check',
  },
  {
    value: 'inactive',
    label: t('TAXIS.DRIVERS.STATUS.INACTIVE'),
    color: 'gray',
    icon: 'i-lucide-circle-x',
  },
  {
    value: 'off_duty',
    label: t('TAXIS.DRIVERS.STATUS.OFF_DUTY'),
    color: 'orange',
    icon: 'i-lucide-moon',
  },
]);

const driverSegmentOptions = computed(() => [
  {
    value: 'limousine',
    label: t('TAXIS.DRIVERS.SEGMENT.LIMOUSINE'),
    color: 'purple',
    icon: 'i-lucide-sparkles',
  },
  {
    value: 'taxis',
    label: t('TAXIS.DRIVERS.SEGMENT.TAXIS'),
    color: 'blue',
    icon: 'i-lucide-car',
  },
  {
    value: 'extended_network',
    label: t('TAXIS.DRIVERS.SEGMENT.EXTENDED_NETWORK'),
    color: 'teal',
    icon: 'i-lucide-network',
  },
]);

const filteredDrivers = computed(() => {
  if (!searchQuery.value.trim()) return drivers.value;
  const q = searchQuery.value.toLowerCase();
  return drivers.value.filter(
    d =>
      d.name?.toLowerCase().includes(q) ||
      d.phone?.toLowerCase().includes(q) ||
      d.license_number?.toLowerCase().includes(q) ||
      d.vehicle_plate?.toLowerCase().includes(q) ||
      (d.taxi_zones || []).some(z => z.name?.toLowerCase().includes(q)) ||
      (d.taxi_partners || []).some(p => p.name?.toLowerCase().includes(q))
  );
});

const resetForm = () => {
  form.value = {
    name: '',
    phone: '',
    driver_segment: 'taxis',
    license_number: '',
    vehicle_plate: '',
    status: 'active',
    notes: '',
  };
  selectedZones.value = [];
  selectedPartners.value = [];
  editing.value = null;
};

const fetchDrivers = async () => {
  loading.value = true;
  try {
    const { data } = await TaxiDriversAPI.getAll();
    drivers.value = data;
  } catch {
    useAlert(t('TAXIS.DRIVERS.API.ERROR_FETCH'));
  } finally {
    loading.value = false;
  }
};

const fetchZonesCatalog = async () => {
  try {
    const { data } = await TaxiZonesAPI.getAll();
    zonesCatalog.value = (data || []).map(z => ({ id: z.id, name: z.name }));
  } catch {
    zonesCatalog.value = [];
  }
};

const fetchPartnersCatalog = async () => {
  try {
    const { data } = await TaxiPartnersAPI.getAll();
    partnersCatalog.value = (data || []).map(p => ({ id: p.id, name: p.name }));
  } catch {
    partnersCatalog.value = [];
  }
};

onMounted(async () => {
  await Promise.all([
    fetchZonesCatalog(),
    fetchPartnersCatalog(),
    fetchDrivers(),
  ]);
});

const openCreate = () => {
  resetForm();
  driverDialogRef.value?.open();
};

const openEdit = driver => {
  editing.value = driver;
  form.value = {
    name: driver.name || '',
    phone: driver.phone || '',
    driver_segment: driver.driver_segment || 'taxis',
    license_number: driver.license_number || '',
    vehicle_plate: driver.vehicle_plate || '',
    status: driver.status || 'active',
    notes: driver.notes || '',
  };
  syncSelectedZonesFromDriver(driver);
  syncSelectedPartnersFromDriver(driver);
  driverDialogRef.value?.open();
};

const onDialogClose = () => {
  resetForm();
};

const closeDialog = () => {
  driverDialogRef.value?.close();
};

const saveDriver = async () => {
  if (!form.value.name.trim()) {
    useAlert(t('TAXIS.DRIVERS.VALIDATION.NAME_REQUIRED'));
    return;
  }
  try {
    const payload = {
      ...form.value,
      taxi_zone_ids: selectedZones.value.map(z => z.id),
      taxi_partner_ids: selectedPartners.value.map(p => p.id),
    };
    if (editing.value) {
      await TaxiDriversAPI.update(editing.value.id, payload);
      useAlert(t('TAXIS.DRIVERS.API.SUCCESS_UPDATE'));
    } else {
      await TaxiDriversAPI.create(payload);
      useAlert(t('TAXIS.DRIVERS.API.SUCCESS_CREATE'));
    }
    closeDialog();
    await fetchDrivers();
  } catch {
    useAlert(t('TAXIS.DRIVERS.API.ERROR_SAVE'));
  }
};

const removeDriver = async driver => {
  // eslint-disable-next-line no-alert, no-restricted-globals
  if (!confirm(t('TAXIS.DRIVERS.CONFIRM_DELETE'))) return;
  try {
    await TaxiDriversAPI.delete(driver.id);
    useAlert(t('TAXIS.DRIVERS.API.SUCCESS_DELETE'));
    await fetchDrivers();
  } catch {
    useAlert(t('TAXIS.DRIVERS.API.ERROR_DELETE'));
  }
};

const statusConfig = status => {
  const opt = statusOptions.value.find(o => o.value === status);
  return (
    opt || {
      label: t('TAXIS.DRIVERS.STATUS.ACTIVE'),
      color: 'gray',
      icon: 'i-lucide-circle',
    }
  );
};

const segmentConfig = segment => {
  const opt = driverSegmentOptions.value.find(o => o.value === segment);
  return (
    opt || {
      label: t('TAXIS.DRIVERS.SEGMENT.TAXIS'),
      color: 'blue',
      icon: 'i-lucide-car',
    }
  );
};

const zonesCellTitle = driver =>
  (driver.taxi_zones || []).map(z => z.name).join(', ');

const partnersCellTitle = driver =>
  (driver.taxi_partners || []).map(p => p.name).join(', ');
</script>

<template>
  <div class="flex flex-1 flex-col overflow-auto px-6 py-8">
    <div class="mx-auto flex w-full max-w-5xl flex-1 flex-col gap-6">
      <div class="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div class="flex items-center gap-3">
            <div
              class="flex size-10 items-center justify-center rounded-xl bg-gradient-to-br from-n-iris-4/50 to-n-iris-5/40 ring-1 ring-n-iris-6/60"
            >
              <Icon icon="i-lucide-users" class="size-5 text-n-iris-11" />
            </div>
            <h1 class="text-xl font-semibold text-n-slate-12">
              {{ t('TAXIS.DRIVERS.TITLE') }}
            </h1>
          </div>
          <p class="mt-2 text-sm text-n-slate-11">
            {{ t('TAXIS.DRIVERS.SUBTITLE') }}
          </p>
        </div>
        <Button
          :label="t('TAXIS.DRIVERS.ADD_BUTTON')"
          icon="i-lucide-plus"
          size="sm"
          color="blue"
          @click="openCreate"
        />
      </div>

      <div class="max-w-sm">
        <Input
          v-model="searchQuery"
          type="search"
          :placeholder="t('TAXIS.DRIVERS.SEARCH_PLACEHOLDER')"
          :custom-input-class="[
            'h-9 [&:not(.focus)]:!border-transparent [&:not(.focus)]:!outline-transparent bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-9',
          ]"
        >
          <template #prefix>
            <Icon
              icon="i-lucide-search"
              class="pointer-events-none absolute top-1/2 size-4 -translate-y-1/2 text-n-slate-11 ltr:left-2.5 rtl:right-2.5"
            />
          </template>
        </Input>
      </div>

      <div
        v-if="loading"
        class="flex flex-1 items-center justify-center py-20 text-n-slate-11"
      >
        <Spinner />
      </div>

      <div
        v-else-if="!drivers.length"
        class="flex flex-col items-center justify-center gap-4 rounded-2xl border-2 border-dashed border-n-iris-5/30 bg-gradient-to-br from-n-iris-1/30 to-transparent py-20 text-center"
      >
        <div
          class="flex size-20 items-center justify-center rounded-2xl bg-gradient-to-br from-n-iris-4/40 to-n-iris-5/30 ring-4 ring-n-iris-4/20"
        >
          <Icon icon="i-lucide-users" class="size-10 text-n-iris-11" />
        </div>
        <div>
          <p class="text-lg font-semibold text-n-slate-12">
            {{ t('TAXIS.DRIVERS.EMPTY_TITLE') }}
          </p>
          <p class="mt-2 max-w-sm text-sm text-n-slate-11">
            {{ t('TAXIS.DRIVERS.EMPTY_SUBTITLE') }}
          </p>
        </div>
        <Button
          :label="t('TAXIS.DRIVERS.ADD_BUTTON')"
          icon="i-lucide-plus"
          size="md"
          color="blue"
          @click="openCreate"
        />
      </div>

      <div
        v-else
        class="overflow-hidden rounded-xl border border-n-weak bg-n-solid-1"
      >
        <div class="overflow-x-auto">
          <table class="w-full min-w-[1180px] text-left text-sm">
            <thead
              class="border-b-2 border-n-iris-6/30 bg-gradient-to-r from-n-iris-2/40 to-n-iris-3/20 text-xs font-semibold uppercase tracking-wide text-n-slate-12"
            >
              <tr>
                <th class="px-4 py-3.5">
                  <div class="flex items-center gap-1.5">
                    <Icon
                      icon="i-lucide-user"
                      class="size-3.5 text-n-iris-10"
                    />
                    {{ t('TAXIS.DRIVERS.COLUMNS.NAME') }}
                  </div>
                </th>
                <th class="px-4 py-3.5">
                  <div class="flex items-center gap-1.5">
                    <Icon
                      icon="i-lucide-phone"
                      class="size-3.5 text-n-blue-10"
                    />
                    {{ t('TAXIS.DRIVERS.COLUMNS.PHONE') }}
                  </div>
                </th>
                <th class="px-4 py-3.5">
                  <div class="flex items-center gap-1.5">
                    <Icon
                      icon="i-lucide-tag"
                      class="size-3.5 text-n-purple-10"
                    />
                    {{ t('TAXIS.DRIVERS.COLUMNS.SEGMENT') }}
                  </div>
                </th>
                <th class="px-4 py-3.5">
                  <div class="flex items-center gap-1.5">
                    <Icon
                      icon="i-lucide-badge"
                      class="size-3.5 text-n-amber-10"
                    />
                    {{ t('TAXIS.DRIVERS.COLUMNS.LICENSE') }}
                  </div>
                </th>
                <th class="px-4 py-3.5">
                  <div class="flex items-center gap-1.5">
                    <Icon icon="i-lucide-car" class="size-3.5 text-n-teal-10" />
                    {{ t('TAXIS.DRIVERS.COLUMNS.VEHICLE') }}
                  </div>
                </th>
                <th class="px-4 py-3.5">
                  <div class="flex items-center gap-1.5">
                    <Icon
                      icon="i-lucide-map-pin"
                      class="size-3.5 text-n-ruby-10"
                    />
                    {{ t('TAXIS.DRIVERS.COLUMNS.ZONES') }}
                  </div>
                </th>
                <th class="px-4 py-3.5">
                  <div class="flex items-center gap-1.5">
                    <Icon
                      icon="i-lucide-handshake"
                      class="size-3.5 text-n-green-10"
                    />
                    {{ t('TAXIS.DRIVERS.COLUMNS.PARTNERS') }}
                  </div>
                </th>
                <th class="px-4 py-3.5">
                  <div class="flex items-center gap-1.5">
                    <Icon
                      icon="i-lucide-activity"
                      class="size-3.5 text-n-green-10"
                    />
                    {{ t('TAXIS.DRIVERS.COLUMNS.STATUS') }}
                  </div>
                </th>
                <th class="px-4 py-3.5 text-end">
                  {{ t('TAXIS.DRIVERS.COLUMNS.ACTIONS') }}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak">
              <tr
                v-for="driver in filteredDrivers"
                :key="driver.id"
                class="transition-colors hover:bg-gradient-to-r hover:from-n-iris-2/10 hover:to-transparent"
              >
                <td class="px-4 py-3.5">
                  <div class="flex items-center gap-2">
                    <div
                      class="flex size-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-n-iris-4/40 to-n-iris-5/30 ring-1 ring-n-iris-6/50"
                    >
                      <Icon
                        icon="i-lucide-user"
                        class="size-4 text-n-iris-11"
                      />
                    </div>
                    <span class="font-semibold text-n-slate-12">{{
                      driver.name
                    }}</span>
                  </div>
                </td>
                <td class="px-4 py-3.5">
                  <div
                    v-if="driver.phone"
                    class="inline-flex items-center gap-1.5 rounded-md bg-n-blue-2/50 px-2 py-1 text-sm text-n-blue-11"
                  >
                    <Icon icon="i-lucide-phone" class="size-3.5" />
                    {{ driver.phone }}
                  </div>
                  <span v-else class="text-n-slate-10">{{
                    t('TAXIS.HISTORY.DASH')
                  }}</span>
                </td>
                <td class="px-4 py-3.5">
                  <span
                    class="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold"
                    :class="[
                      segmentConfig(driver.driver_segment).color === 'purple'
                        ? 'bg-n-purple-3/50 text-n-purple-11 ring-1 ring-n-purple-6/50'
                        : segmentConfig(driver.driver_segment).color === 'blue'
                          ? 'bg-n-blue-3/50 text-n-blue-11 ring-1 ring-n-blue-6/50'
                          : 'bg-n-teal-3/50 text-n-teal-11 ring-1 ring-n-teal-6/50',
                    ]"
                  >
                    <Icon
                      :icon="segmentConfig(driver.driver_segment).icon"
                      class="size-3.5"
                    />
                    {{ segmentConfig(driver.driver_segment).label }}
                  </span>
                </td>
                <td class="px-4 py-3.5">
                  <div
                    v-if="driver.license_number"
                    class="inline-flex items-center gap-1.5 rounded-md bg-n-amber-2/50 px-2 py-1 font-mono text-xs text-n-amber-11"
                  >
                    <Icon icon="i-lucide-badge" class="size-3.5" />
                    {{ driver.license_number }}
                  </div>
                  <span v-else class="text-n-slate-10">{{
                    t('TAXIS.HISTORY.DASH')
                  }}</span>
                </td>
                <td class="px-4 py-3.5">
                  <div
                    v-if="driver.vehicle_plate"
                    class="inline-flex items-center gap-1.5 rounded-md bg-n-teal-2/50 px-2.5 py-1 font-mono text-sm font-bold uppercase text-n-teal-11"
                  >
                    <Icon icon="i-lucide-car" class="size-3.5" />
                    {{ driver.vehicle_plate }}
                  </div>
                  <span v-else class="text-n-slate-10">{{
                    t('TAXIS.HISTORY.DASH')
                  }}</span>
                </td>
                <td class="px-4 py-3.5">
                  <div
                    v-if="driver.taxi_zones?.length"
                    class="flex flex-wrap gap-1"
                  >
                    <span
                      v-for="zone in driver.taxi_zones.slice(0, 2)"
                      :key="zone.id"
                      class="inline-flex items-center gap-1 rounded-md bg-n-ruby-2/40 px-2 py-0.5 text-xs text-n-ruby-11"
                    >
                      <Icon icon="i-lucide-map-pin" class="size-3" />
                      {{ zone.name }}
                    </span>
                    <span
                      v-if="driver.taxi_zones.length > 2"
                      class="inline-flex items-center rounded-md bg-n-ruby-2/40 px-2 py-0.5 text-xs font-medium text-n-ruby-11"
                      :title="zonesCellTitle(driver)"
                    >
                      {{
                        t('TAXIS.COMMON.MORE_COUNT', {
                          count: driver.taxi_zones.length - 2,
                        })
                      }}
                    </span>
                  </div>
                  <span v-else class="text-n-slate-10">{{
                    t('TAXIS.HISTORY.DASH')
                  }}</span>
                </td>
                <td class="px-4 py-3.5">
                  <div
                    v-if="driver.taxi_partners?.length"
                    class="flex flex-wrap gap-1"
                  >
                    <span
                      v-for="partner in driver.taxi_partners.slice(0, 2)"
                      :key="partner.id"
                      class="inline-flex items-center gap-1 rounded-md bg-n-green-2/40 px-2 py-0.5 text-xs text-n-green-11"
                    >
                      <Icon icon="i-lucide-handshake" class="size-3" />
                      {{ partner.name }}
                    </span>
                    <span
                      v-if="driver.taxi_partners.length > 2"
                      class="inline-flex items-center rounded-md bg-n-green-2/40 px-2 py-0.5 text-xs font-medium text-n-green-11"
                      :title="partnersCellTitle(driver)"
                    >
                      {{
                        t('TAXIS.COMMON.MORE_COUNT', {
                          count: driver.taxi_partners.length - 2,
                        })
                      }}
                    </span>
                  </div>
                  <span v-else class="text-n-slate-10">{{
                    t('TAXIS.HISTORY.DASH')
                  }}</span>
                </td>
                <td class="px-4 py-3.5">
                  <span
                    class="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold"
                    :class="[
                      statusConfig(driver.status).color === 'green'
                        ? 'bg-n-green-3/60 text-n-green-11 ring-1 ring-n-green-6/50'
                        : statusConfig(driver.status).color === 'orange'
                          ? 'bg-n-amber-3/60 text-n-amber-11 ring-1 ring-n-amber-6/50'
                          : 'bg-n-slate-3/60 text-n-slate-11 ring-1 ring-n-slate-6/50',
                    ]"
                  >
                    <Icon
                      :icon="statusConfig(driver.status).icon"
                      class="size-3.5"
                    />
                    {{ statusConfig(driver.status).label }}
                  </span>
                </td>
                <td class="px-4 py-3 text-end">
                  <div class="flex justify-end gap-1">
                    <button
                      type="button"
                      class="rounded-lg p-2 text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
                      :title="t('TAXIS.DRIVERS.EDIT')"
                      @click="openEdit(driver)"
                    >
                      <Icon icon="i-lucide-pencil" class="size-4" />
                    </button>
                    <button
                      type="button"
                      class="rounded-lg p-2 text-n-ruby-11 hover:bg-n-ruby-3/30"
                      :title="t('TAXIS.DRIVERS.DELETE')"
                      @click="removeDriver(driver)"
                    >
                      <Icon icon="i-lucide-trash-2" class="size-4" />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div
          v-if="drivers.length && !filteredDrivers.length"
          class="px-4 py-8 text-center text-sm text-n-slate-11"
        >
          {{ t('TAXIS.DRIVERS.NO_SEARCH_RESULTS') }}
        </div>
      </div>
    </div>

    <Dialog
      ref="driverDialogRef"
      type="edit"
      :title="
        editing
          ? t('TAXIS.DRIVERS.DIALOG_EDIT_TITLE')
          : t('TAXIS.DRIVERS.DIALOG_CREATE_TITLE')
      "
      width="lg"
      :show-cancel-button="false"
      :show-confirm-button="false"
      overflow-y-auto
      @close="onDialogClose"
    >
      <div class="flex flex-col gap-4">
        <Input
          v-model="form.name"
          :label="t('TAXIS.DRIVERS.FORM.NAME')"
          :placeholder="t('TAXIS.DRIVERS.FORM.NAME_PLACEHOLDER')"
        />
        <Input
          v-model="form.phone"
          :label="t('TAXIS.DRIVERS.FORM.PHONE')"
          :placeholder="t('TAXIS.DRIVERS.FORM.PHONE_PLACEHOLDER')"
        />
        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('TAXIS.DRIVERS.FORM.DRIVER_SEGMENT') }}
          </label>
          <select
            v-model="form.driver_segment"
            class="h-10 w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12 outline-none focus:border-n-blue-8 focus:ring-1 focus:ring-n-blue-8"
          >
            <option
              v-for="opt in driverSegmentOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
        </div>
        <div class="grid gap-4 sm:grid-cols-2">
          <Input
            v-model="form.license_number"
            :label="t('TAXIS.DRIVERS.FORM.LICENSE')"
            :placeholder="t('TAXIS.DRIVERS.FORM.LICENSE_PLACEHOLDER')"
          />
          <Input
            v-model="form.vehicle_plate"
            :label="t('TAXIS.DRIVERS.FORM.VEHICLE')"
            :placeholder="t('TAXIS.DRIVERS.FORM.VEHICLE_PLACEHOLDER')"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('TAXIS.DRIVERS.FORM.STATUS') }}
          </label>
          <select
            v-model="form.status"
            class="h-10 w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12 outline-none focus:border-n-blue-8 focus:ring-1 focus:ring-n-blue-8"
          >
            <option
              v-for="opt in statusOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
        </div>
        <div class="flex w-full min-w-0 flex-col gap-1.5">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('TAXIS.DRIVERS.FORM.ZONES') }}
          </label>
          <multiselect
            v-model="selectedZones"
            class="[&_.multiselect__tags]:min-h-10"
            :options="zonesCatalog"
            track-by="id"
            label="name"
            multiple
            :close-on-select="false"
            :clear-on-select="false"
            hide-selected
            selected-label
            :placeholder="t('TAXIS.DRIVERS.FORM.ZONES_PLACEHOLDER')"
            :select-label="t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
            :deselect-label="t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
          />
        </div>
        <div class="flex w-full min-w-0 flex-col gap-1.5">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('TAXIS.DRIVERS.FORM.PARTNERS') }}
          </label>
          <multiselect
            v-model="selectedPartners"
            class="[&_.multiselect__tags]:min-h-10"
            :options="partnersCatalog"
            track-by="id"
            label="name"
            multiple
            :close-on-select="false"
            :clear-on-select="false"
            hide-selected
            selected-label
            :placeholder="t('TAXIS.DRIVERS.FORM.PARTNERS_PLACEHOLDER')"
            :select-label="t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
            :deselect-label="t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
          />
        </div>
        <TextArea
          v-model="form.notes"
          :label="t('TAXIS.DRIVERS.FORM.NOTES')"
          :placeholder="t('TAXIS.DRIVERS.FORM.NOTES_PLACEHOLDER')"
          :max-length="2000"
          min-height="5rem"
        />
      </div>
      <template #footer>
        <div class="flex w-full justify-end gap-2">
          <Button
            variant="faded"
            color="slate"
            type="button"
            :label="t('TAXIS.DRIVERS.CANCEL')"
            @click="closeDialog"
          />
          <Button
            type="button"
            :label="t('TAXIS.DRIVERS.SAVE')"
            @click="saveDriver"
          />
        </div>
      </template>
    </Dialog>
  </div>
</template>
